import React, { useState, useEffect, useCallback, useRef } from "react";
import { Camera, CameraStatus, CameraSnapshot } from "../../../types";
import {
  AlertCircle,
  Loader2,
  RefreshCw,
  Monitor,
  HardDrive,
  Video,
  Brain,
} from "lucide-react";

interface CameraStatusTabProps {
  cameras: Camera[];
  cameraStatuses: Record<string, { camera_status: CameraStatus }>;
  cameraSnapshots: Record<string, { snapshot: CameraSnapshot }>;
  cameraErrors?: Record<
    string,
    { statusError?: string; snapshotError?: string }
  >;
  loadingCameras: boolean;
  onRefreshSnapshot?: (cameraId: string) => Promise<void>;
}

const CameraStatusTab: React.FC<CameraStatusTabProps> = ({
  cameras,
  cameraStatuses,
  cameraSnapshots,
  cameraErrors = {},
  loadingCameras,
  onRefreshSnapshot,
}) => {
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const [imageLoading, setImageLoading] = useState<Record<string, boolean>>({});
  const [imageRetries, setImageRetries] = useState<Record<string, number>>({});
  const prevSnapshotKeyRef = useRef<Record<string, string>>({});

  /** Coerce API values (number, string, bigint, or numeric objects) to a finite number. */
  const toFiniteNumber = useCallback((value: unknown): number | undefined => {
    if (value === undefined || value === null || value === "") return undefined;
    if (typeof value === "bigint") {
      const n = Number(value);
      return Number.isFinite(n) ? n : undefined;
    }
    if (typeof value === "string") {
      const n = Number(value.trim().replace(/,/g, ""));
      return Number.isFinite(n) ? n : undefined;
    }
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : undefined;
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }, []);

  // Format storage from MB to GB (API may send strings, objects, etc.)
  const formatStorage = useCallback(
    (mb: unknown) => {
      const n = toFiniteNumber(mb);
      if (n === undefined) return { value: "N/A", unit: "", raw: 0 };
      if (n >= 1024) {
        return { value: (n / 1024).toFixed(2), unit: "GB", raw: n };
      }
      return { value: n.toFixed(1), unit: "MB", raw: n };
    },
    [toFiniteNumber],
  );

  // Handle image load success
  const handleImageLoad = useCallback((cameraId: string) => {
    setImageLoading((prev) => ({ ...prev, [cameraId]: false }));
    setImageErrors((prev) => ({ ...prev, [cameraId]: false }));
  }, []);

  // Handle image error with retry logic
  const handleImageError = useCallback(
    (cameraId: string, snapshot: CameraSnapshot | undefined) => {
      const retries = imageRetries[cameraId] || 0;
      if (retries < 2 && snapshot?.url) {
        // Retry loading the image (create new image element to force reload)
        setImageRetries((prev) => ({ ...prev, [cameraId]: retries + 1 }));
        setTimeout(
          () => {
            // Force reload by adding timestamp
            const img = new Image();
            img.src =
              snapshot.url +
              (snapshot.url.includes("?") ? "&" : "?") +
              `_retry=${Date.now()}`;
            img.onload = () => handleImageLoad(cameraId);
            img.onerror = () => {
              if (retries + 1 >= 2) {
                setImageErrors((prev) => ({ ...prev, [cameraId]: true }));
                setImageLoading((prev) => ({ ...prev, [cameraId]: false }));
              }
            };
          },
          1000 * (retries + 1),
        );
      } else {
        setImageErrors((prev) => ({ ...prev, [cameraId]: true }));
        setImageLoading((prev) => ({ ...prev, [cameraId]: false }));
      }
    },
    [imageRetries, handleImageLoad],
  );

  // Reset image state only when *that camera's* snapshot changes.
  // Otherwise, when a second camera's snapshot arrives, we would incorrectly
  // flip other cameras back into "loading" (grey overlay) without a new <img> load.
  useEffect(() => {
    const updates: Array<{
      cameraId: string;
      nextKey: string;
      hasSnapshot: boolean;
    }> = [];

    cameras.forEach((camera) => {
      const snapshot = cameraSnapshots[camera.id]?.snapshot;
      const nextKey = snapshot?.timestamp || snapshot?.url || "";
      const prevKey = prevSnapshotKeyRef.current[camera.id] || "";

      if (nextKey && nextKey !== prevKey) {
        updates.push({
          cameraId: camera.id,
          nextKey,
          hasSnapshot: true,
        });
      } else if (!nextKey && prevKey) {
        // Snapshot removed / unavailable now
        updates.push({
          cameraId: camera.id,
          nextKey: "",
          hasSnapshot: false,
        });
      }
    });

    if (updates.length === 0) return;

    // Apply batched state updates
    setImageErrors((prev) => {
      const next = { ...prev };
      updates.forEach((u) => {
        next[u.cameraId] = false;
      });
      return next;
    });
    setImageRetries((prev) => {
      const next = { ...prev };
      updates.forEach((u) => {
        next[u.cameraId] = 0;
      });
      return next;
    });
    setImageLoading((prev) => {
      const next = { ...prev };
      updates.forEach((u) => {
        next[u.cameraId] = u.hasSnapshot ? true : false;
      });
      return next;
    });

    // Update ref after we schedule state updates
    updates.forEach((u) => {
      prevSnapshotKeyRef.current[u.cameraId] = u.nextKey;
    });
  }, [cameras, cameraSnapshots]);

  if (cameras.length === 0 && !loadingCameras) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-center py-12">
          <AlertCircle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Cameras Bound
          </h3>
          <p className="text-gray-500">
            This device does not have any cameras bound to it.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {cameras.map((camera) => {
        const status = cameraStatuses[camera.id]?.camera_status;
        const snapshot = cameraSnapshots[camera.id]?.snapshot;
        const isLoadingSnapshot = !snapshot && loadingCameras;
        const hasImageError = imageErrors[camera.id];
        const errors = cameraErrors[camera.id];
        const statusError = errors?.statusError;
        const snapshotError = errors?.snapshotError;

        // Determine error type and message
        const getErrorMessage = () => {
          // Combine error messages for analysis
          const combinedError = `${statusError || ""} ${
            snapshotError || ""
          }`.toLowerCase();

          // Check for offline errors (highest priority)
          if (
            combinedError.includes("offline") ||
            combinedError.includes("device offline")
          ) {
            return {
              primary: "Camera offline",
              secondary:
                "The camera device is currently offline and cannot be reached.",
            };
          }
          // Check for 503/502 errors (service unavailable/bad gateway)
          if (
            statusError?.includes("503") ||
            snapshotError?.includes("503") ||
            statusError?.includes("502") ||
            snapshotError?.includes("502")
          ) {
            return {
              primary: "Camera offline",
              secondary:
                "The camera device is currently offline and cannot be reached.",
            };
          }
          // Check for not synced errors
          if (
            combinedError.includes("not yet synced") ||
            combinedError.includes("not synced")
          ) {
            return {
              primary: "Camera not synced",
              secondary:
                "Camera data has not yet been synced, please try again later.",
            };
          }
          // Check if we have any error
          if (statusError || snapshotError) {
            // If we have a specific error message, use it
            if (snapshotError) {
              return {
                primary: "Snapshot unavailable",
                secondary: snapshotError,
              };
            }
            if (statusError) {
              return {
                primary: "Status unavailable",
                secondary: statusError,
              };
            }
          }
          // If no snapshot and no error yet, return null (will show generic message)
          return null;
        };

        const errorInfo = getErrorMessage();

        // Format storage values
        const freeStorage = formatStorage(status?.storage?.free_mb);
        const totalStorage = formatStorage(status?.storage?.total_mb);
        const usedPercent = toFiniteNumber(status?.storage?.used_percent);

        return (
          <div
            key={camera.id}
            className="bg-white rounded-lg border border-gray-200 p-6"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-end">
              {/* Snapshot on the left */}
              <div className="flex flex-col h-full">
                {isLoadingSnapshot ? (
                  <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg border border-gray-200">
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-2" />
                      <p className="text-sm text-gray-500">
                        Loading snapshot...
                      </p>
                    </div>
                  </div>
                ) : snapshot?.url ? (
                  <div className="relative w-full overflow-hidden flex items-center justify-center group">
                    {imageLoading[camera.id] && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 z-10 rounded-lg border border-gray-200 min-h-[384px]">
                        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                      </div>
                    )}
                    {hasImageError && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white z-10 rounded-lg border border-gray-200 min-h-[384px]">
                        <div className="text-center px-4">
                          <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-500 mb-1 font-medium">
                            {errorInfo?.primary || "Snapshot not available"}
                          </p>
                          {errorInfo?.secondary && (
                            <p className="text-xs text-gray-400 mb-3">
                              {errorInfo.secondary}
                            </p>
                          )}
                          {onRefreshSnapshot && (
                            <button
                              onClick={() => onRefreshSnapshot(camera.id)}
                              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 mx-auto"
                            >
                              <RefreshCw className="w-3 h-3" />
                              Retry
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                    <img
                      key={`${camera.id}-${
                        snapshot?.timestamp || snapshot?.url || "none"
                      }-${imageRetries[camera.id] || 0}`}
                      src={snapshot.url}
                      alt={`Camera ${camera.name || camera.cloud_id} snapshot`}
                      className={`w-full h-auto object-contain transition-opacity duration-300 rounded-lg ${
                        imageLoading[camera.id] || hasImageError
                          ? "opacity-0"
                          : "opacity-100"
                      }`}
                      onLoad={() => handleImageLoad(camera.id)}
                      onError={() => handleImageError(camera.id, snapshot)}
                    />
                    {onRefreshSnapshot && !imageLoading[camera.id] && (
                      <button
                        onClick={() => onRefreshSnapshot(camera.id)}
                        className="absolute bottom-2 right-2 bg-white bg-opacity-90 hover:bg-opacity-100 p-2 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Refresh snapshot"
                      >
                        <RefreshCw className="w-4 h-4 text-gray-600" />
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg border border-gray-200">
                    <div className="text-center px-4">
                      <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 mb-1 font-medium">
                        {errorInfo?.primary || "Snapshot not available"}
                      </p>
                      {errorInfo?.secondary ? (
                        <p className="text-xs text-gray-400 mt-1">
                          {errorInfo.secondary}
                        </p>
                      ) : (
                        !snapshot && (
                          <p className="text-xs text-gray-400 mt-1">
                            Camera may not be synced yet
                          </p>
                        )
                      )}
                      {onRefreshSnapshot && (
                        <button
                          onClick={() => onRefreshSnapshot(camera.id)}
                          className="mt-3 text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 mx-auto"
                        >
                          <RefreshCw className="w-3 h-3" />
                          Retry
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Info sections on the right - 2x2 grid */}
              <div className="grid grid-cols-2 gap-4">
                {/* System Info - Top Left */}
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Monitor className="w-4 h-4 text-gray-600" />
                    <h4 className="font-medium text-gray-700 text-sm">
                      System Info
                    </h4>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-600">Model:</span>
                      <span className="text-xs text-gray-900">
                        {status?.device?.model || camera?.model || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between items-start">
                      <span className="text-xs text-gray-600">Firmware:</span>
                      <span className="text-xs text-gray-900 text-right break-words max-w-[60%]">
                        {status?.device?.firmware || camera?.firmware || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-600">MAC:</span>
                      <span className="text-xs text-gray-900 font-mono">
                        {status?.device?.mac || camera?.mac || "N/A"}
                      </span>
                    </div>
                    {status?.device?.release_date && (
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-600">
                          Release Date:
                        </span>
                        <span className="text-xs text-gray-900">
                          {new Date(
                            status?.device?.release_date,
                          ).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {status?.last_updated_iso && (
                      <div className="flex justify-between items-start">
                        <span className="text-xs text-gray-600">
                          Last Updated:
                        </span>
                        <span className="text-xs text-gray-900 text-right break-words max-w-[60%]">
                          {new Date(status.last_updated_iso).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Storage - Top Right */}
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <HardDrive className="w-4 h-4 text-gray-600" />
                    <h4 className="font-medium text-gray-700 text-sm">
                      Storage
                    </h4>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">Status:</span>
                      <span
                        className={`text-xs font-medium ${
                          status?.storage?.status === "ok" ||
                          status?.storage?.status === "normal"
                            ? "text-green-600"
                            : status?.storage?.status === "warning"
                              ? "text-yellow-600"
                              : status?.storage?.status === "error"
                                ? "text-red-600"
                                : "text-gray-500"
                        }`}
                      >
                        {status?.storage?.status || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">Free:</span>
                      <span className="text-xs text-gray-900 font-medium">
                        {freeStorage.value} {freeStorage.unit}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">Total:</span>
                      <span className="text-xs text-gray-900 font-medium">
                        {totalStorage.value} {totalStorage.unit}
                      </span>
                    </div>
                    {usedPercent !== undefined && (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">Used:</span>
                          <span className="text-xs text-gray-900 font-medium">
                            {usedPercent.toFixed(1)}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                          <div
                            className={`h-2 rounded-full transition-all ${
                              usedPercent < 50
                                ? "bg-green-500"
                                : usedPercent < 80
                                  ? "bg-yellow-500"
                                  : "bg-red-500"
                            }`}
                            style={{
                              width: `${Math.min(usedPercent, 100)}%`,
                            }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Video & Audio - Bottom Left */}
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Video className="w-4 h-4 text-gray-600" />
                    <h4 className="font-medium text-gray-700 text-sm">
                      Video & Audio
                    </h4>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-600">Resolution:</span>
                      <span className="text-xs text-gray-900">
                        {status?.stream?.resolution ||
                          camera?.stream?.resolution ||
                          "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-600">FPS:</span>
                      <span className="text-xs text-gray-900">
                        {status?.stream?.fps !== undefined
                          ? `${status.stream.fps}`
                          : camera?.stream?.fps !== undefined
                            ? `${camera.stream.fps}`
                            : "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-600">Bitrate:</span>
                      <span className="text-xs text-gray-900">
                        {status?.stream?.bitrate !== undefined
                          ? `${status.stream.bitrate} kbps`
                          : camera?.stream?.bitrate !== undefined
                            ? `${camera.stream.bitrate} kbps`
                            : "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-600">Audio:</span>
                      <span
                        className={`text-xs font-medium ${
                          status?.stream?.audio_enabled !== null
                            ? status?.stream?.audio_enabled
                              ? "text-green-600"
                              : "text-gray-500"
                            : camera?.stream?.audio_enabled !== null
                              ? camera?.stream?.audio_enabled
                                ? "text-green-600"
                                : "text-gray-500"
                              : "text-gray-500"
                        }`}
                      >
                        {status?.stream?.audio_enabled !== null
                          ? status?.stream?.audio_enabled
                            ? "Enabled"
                            : "Disabled"
                          : camera?.stream?.audio_enabled !== null
                            ? camera?.stream?.audio_enabled
                              ? "Enabled"
                              : "Disabled"
                            : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* AI Status - Bottom Right */}
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Brain className="w-4 h-4 text-gray-600" />
                    <h4 className="font-medium text-gray-700 text-sm">
                      AI Status
                    </h4>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-600">
                        Motion Detect:
                      </span>
                      <span
                        className={`text-xs font-medium ${
                          status?.analytics?.motion_detection !== null
                            ? status?.analytics?.motion_detection
                              ? "text-green-600"
                              : "text-gray-500"
                            : camera?.analytics?.motion_detection !== null
                              ? camera?.analytics?.motion_detection
                                ? "text-green-600"
                                : "text-gray-500"
                              : "text-gray-500"
                        }`}
                      >
                        {status?.analytics?.motion_detection !== null
                          ? status?.analytics?.motion_detection
                            ? "Enabled"
                            : "Disabled"
                          : camera?.analytics?.motion_detection !== null
                            ? camera?.analytics?.motion_detection
                              ? "Enabled"
                              : "Disabled"
                            : "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-600">
                        Face Detect:
                      </span>
                      <span
                        className={`text-xs font-medium ${
                          status?.analytics?.face_detection !== null
                            ? status?.analytics?.face_detection
                              ? "text-green-600"
                              : "text-gray-500"
                            : camera?.analytics?.face_detection !== null
                              ? camera?.analytics?.face_detection
                                ? "text-green-600"
                                : "text-gray-500"
                              : "text-gray-500"
                        }`}
                      >
                        {status?.analytics?.face_detection !== null
                          ? status?.analytics?.face_detection
                            ? "Enabled"
                            : "Disabled"
                          : camera?.analytics?.face_detection !== null
                            ? camera?.analytics?.face_detection
                              ? "Enabled"
                              : "Disabled"
                            : "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-600">
                        Line Crossing:
                      </span>
                      <span
                        className={`text-xs font-medium ${
                          status?.analytics?.line_crossing !== null
                            ? status?.analytics?.line_crossing
                              ? "text-green-600"
                              : "text-gray-500"
                            : camera?.analytics?.line_crossing !== null
                              ? camera?.analytics?.line_crossing
                                ? "text-green-600"
                                : "text-gray-500"
                              : "text-gray-500"
                        }`}
                      >
                        {status?.analytics?.line_crossing !== null
                          ? status?.analytics?.line_crossing
                            ? "Enabled"
                            : "Disabled"
                          : camera?.analytics?.line_crossing !== null
                            ? camera?.analytics?.line_crossing
                              ? "Enabled"
                              : "Disabled"
                            : "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-600">
                        Intrusion Detect:
                      </span>
                      <span
                        className={`text-xs font-medium ${
                          status?.analytics?.intrusion_detection !== null
                            ? status?.analytics?.intrusion_detection
                              ? "text-green-600"
                              : "text-gray-500"
                            : camera?.analytics?.intrusion_detection !== null
                              ? camera?.analytics?.intrusion_detection
                                ? "text-green-600"
                                : "text-gray-500"
                              : "text-gray-500"
                        }`}
                      >
                        {status?.analytics?.intrusion_detection !== null
                          ? status?.analytics?.intrusion_detection
                            ? "Enabled"
                            : "Disabled"
                          : camera?.analytics?.intrusion_detection !== null
                            ? camera?.analytics?.intrusion_detection
                              ? "Enabled"
                              : "Disabled"
                            : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CameraStatusTab;
